import React, { memo } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Skeleton } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

import CurrencyFormatter from '@/components/currency-formatter'
import { ParagraphRegular } from '@/components/text'

import type { PricingDisplayFrequency } from './types'

type Styles = {}

interface RangePrice {
  min: string
  max: string
}

type Props = {
  price: string | RangePrice
  frequency: PricingDisplayFrequency
  showRange?: boolean
  fallback?: boolean | JSX.Element
  loading?: boolean
  priceStyles?: Styles
  frequencyStyles?: Styles
  center?: boolean
  isLockedMls?: boolean
}

type RangePriceProps = {
  price: RangePrice
  priceStyles?: Styles
  locale?: string
}
type RegularPriceProps = {
  price: string | RangePrice
  frequency: PricingDisplayFrequency
  priceStyles?: Styles
  locale?: string
}

const Price = styled(ParagraphRegular)<{
  center?: boolean
  frequencyStyles?: Styles
}>`
  line-height: 20px;
  color: ${vars.foregroundColor.secondary};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  ${props =>
    props.center &&
    `
    display: flex;
    align-items: center;
    gap: 4px;
  `}

  ${props => props.frequencyStyles}
`

const Amount = styled.span<{
  priceStyles?: Styles
}>`
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: ${vars.foregroundColor.neutral};

  ${props => props.priceStyles}
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  align-items: baseline;
  width: 100%;
`

const RangePrice = ({ price, priceStyles, locale }: RangePriceProps) => {
  return (
    <Amount id="listing-card-price" priceStyles={priceStyles}>
      <CurrencyFormatter value={price.min} locale={locale} /> -{' '}
      <CurrencyFormatter value={price.max} locale={locale} />
    </Amount>
  )
}

const RegularPrice = ({
  price,
  frequency,
  priceStyles,
  locale
}: RegularPriceProps) => {
  const { t } = useTranslation()
  const isMultiListing = typeof price === 'object'

  return (
    <>
      <Amount id="listing-card-price" priceStyles={priceStyles}>
        <CurrencyFormatter
          value={isMultiListing && price !== null ? price.min : price}
          locale={locale}
        />
        {isMultiListing && '+'}
      </Amount>
      {` / ${t(`b.listing.view.price_frequency_${frequency}.text`)}`}
    </>
  )
}

const PriceDisplay: React.FC<Props> = memo(
  ({
    price,
    frequency,
    showRange,
    fallback,
    loading,
    priceStyles,
    frequencyStyles,
    center
  }) => {
    const router = useRouter()
    const locale = router?.locale
    if (loading) {
      return (
        <Column>
          <Skeleton.Shape height={28} width={250} />
        </Column>
      )
    }

    return (
      <Column>
        <Row>
          <Price frequencyStyles={frequencyStyles} center={center}>
            {fallback ? (
              <Amount id="listing-card-price" priceStyles={priceStyles}>
                {fallback}
              </Amount>
            ) : (
              <>
                {showRange && typeof price === 'object' ? (
                  <RangePrice
                    price={price}
                    priceStyles={priceStyles}
                    locale={locale}
                  />
                ) : (
                  <RegularPrice
                    price={price}
                    frequency={frequency}
                    priceStyles={priceStyles}
                    locale={locale}
                  />
                )}
              </>
            )}
          </Price>
        </Row>
      </Column>
    )
  }
)

PriceDisplay.displayName = 'PriceDisplay'

export default PriceDisplay
